import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ShieldCheck,
  Cpu,
  Database,
  Workflow,
  GraduationCap,
  Map,
  FolderOpen,
  Download,
} from "lucide-react";
import ScoreGaugePanel from "@/components/ruby-ai-v2/dashboard/ScoreGaugePanel";
import type { SubNavItem } from "@/components/ruby-ai-v2/dashboard/ScoreGaugePanel";
import ActionPlanIssuesPanel from "@/components/ruby-ai-v2/dashboard/ActionPlanIssuesPanel";
import AskRubyAIReadinessWidget from "@/components/ruby-ai-v2/dashboard/AskRubyAIReadinessWidget";
import EmptyState from "@/components/ruby-ai-v2/dashboard/EmptyState";
import DataPanel from "@/components/ruby-ai-v2/dashboard/DataPanel";
import WidgetExportBtn from "@/components/ruby-ai-v2/reports/WidgetExportBtn";
import { flattenAIEngines, flattenLeaderboard } from "@/components/ruby-ai-v2/reports/exportDataUtils";
import { loadLatestAudits, loadLatestAuditByShortcut } from "@/components/ruby-ai-v2/services/auditStorageService";
import SiteSwitcher from "@/components/ruby-ai-v2/dashboard/SiteSwitcher";
import ReportSwitcher from "@/components/ruby-ai-v2/dashboard/ReportSwitcher";
import { useSiteContext } from "@/components/ruby-ai-v2/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import RubyExportDialog from "@/components/ruby-ai-v2/reports/RubyExportDialog";
import { buildFullExportData } from "@/components/ruby-ai-v2/reports/exportDataUtils";

const tabs = [
  { id: "overview", label: "Overview", icon: ShieldCheck },
  { id: "technology", label: "Technology", icon: Cpu },
  { id: "data", label: "Data", icon: Database },
  { id: "processes", label: "Processes", icon: Workflow },
  { id: "skills", label: "Skills", icon: GraduationCap },
  { id: "roadmap", label: "Roadmap", icon: Map },
] as const;

type TabId = (typeof tabs)[number]["id"];

function formatTimeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

const heroDefaults: Record<TabId, {
  badge: string; title: string; centerLabel: string; subtitle: string;
  metricLabels: string[];
}> = {
  overview: { badge: "AI Readiness", title: "AI Readiness Score", centerLabel: "READINESS", subtitle: "Run an AI Maturity Assessment to see your overall readiness.", metricLabels: ["Brand Presence", "Link Presence", "Avg Position", "Engines Tracked"] },
  technology: { badge: "AI Engines", title: "AI Engine Score", centerLabel: "ENGINES", subtitle: "Run an AI Engine Comparison to see engine-level metrics.", metricLabels: ["Engines", "Best Engine", "Avg. Brand %", "Avg. Link %"] },
  data: { badge: "Content Alignment", title: "Content AI Score", centerLabel: "CONTENT", subtitle: "Run a Content AI Alignment audit to assess content readiness.", metricLabels: ["Brand Presence", "Link Presence", "Position", "Competitors"] },
  processes: { badge: "AEO Strategy", title: "AEO Score", centerLabel: "AEO", subtitle: "Run an AEO Strategy audit to assess your AI engine optimisation.", metricLabels: ["Brand Presence", "Link Presence", "Position", "Leaderboard"] },
  skills: { badge: "AI Maturity", title: "AI Maturity Score", centerLabel: "MATURITY", subtitle: "Run an AI Maturity Assessment to see your maturity level.", metricLabels: ["Brand Presence", "Link Presence", "Position", "Engines"] },
  roadmap: { badge: "AI Strategy", title: "AI Strategy Score", centerLabel: "STRATEGY", subtitle: "Run an AEO Strategy Planner to build your roadmap.", metricLabels: ["Strategy Score", "Actions", "Priority Items", "Timeline"] },
};

const TAB_SUBNAV: Record<TabId, Omit<SubNavItem, "hasData">[]> = {
  overview: [
    { id: "maturity", label: "AI Maturity", shortcutId: "ai-maturity" },
    { id: "visibility", label: "AI Visibility", shortcutId: "ai-visibility" },
    { id: "citation", label: "AI Citation", shortcutId: "ai-citation" },
  ],
  technology: [
    { id: "engine-comp", label: "Engine Comparison", shortcutId: "ai-engine-comparison" },
    { id: "prompt-track", label: "Prompt Tracking", shortcutId: "ai-prompt-tracking" },
  ],
  data: [
    { id: "content-align", label: "Content Alignment", shortcutId: "content-alignment" },
    { id: "vis-data", label: "AI Visibility", shortcutId: "ai-visibility" },
  ],
  processes: [
    { id: "aeo", label: "AEO Strategy", shortcutId: "aeo-strategy" },
  ],
  skills: [
    { id: "maturity-skills", label: "AI Maturity", shortcutId: "ai-maturity" },
  ],
  roadmap: [
    { id: "aeo-road", label: "AEO Strategy", shortcutId: "aeo-strategy" },
    { id: "prompt-road", label: "Prompt Tracking", shortcutId: "ai-prompt-tracking" },
  ],
};


const MetricCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
    <div className="text-xs mb-1" style={{ color: "#9AA0B3" }}>{label}</div>
    <div className="text-xl font-bold" style={{ color: highlight ? "#FF9A4A" : "#E8E4FF" }}>{value}</div>
  </div>
);

const AIReadinessDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { activeSite, selectedReportId } = useSiteContext();
  const { user } = useAuth();
  const userId = user?.id;
  const auditContextKey = JSON.stringify([userId, activeSite?.domain, selectedReportId]);
  const auditContextRef = useRef(auditContextKey);
  auditContextRef.current = auditContextKey;
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [liveAuditData, setLiveAuditData] = useState<any | null>(null);
  const [activeSubNav, setActiveSubNav] = useState<string>(TAB_SUBNAV.overview[0].id);
  const [availableAudits, setAvailableAudits] = useState<Set<string>>(new Set());
  const [lastAuditMeta, setLastAuditMeta] = useState<{ region: string; domain: string; createdAt: string } | null>(null);
  const [exportDialog, setExportDialog] = useState<{ open: boolean; title: string; tier: "widget" | "chat" | "full"; data: any[]; auditResult?: any }>({ open: false, title: "", tier: "widget", data: [] });

  const openFullReportExport = () => {
    if (!liveAuditData) return;
    setExportDialog({ open: true, title: "AI Readiness Full Report", tier: "full", data: buildFullExportData(liveAuditData, liveAuditData.requestedAuditType || "ai-maturity"), auditResult: liveAuditData });
  };

  useEffect(() => {
    const firstItem = TAB_SUBNAV[activeTab]?.[0];
    if (firstItem) setActiveSubNav(firstItem.id);
  }, [activeTab]);

  const loadDashboardAudit = async (domain?: string) => {
    if (!userId || !domain) return;
    setLiveAuditData(null);
    setLastAuditMeta(null);
    const audit = await loadLatestAuditByShortcut({ userId, cardId: "ai-readiness", shortcutId: "authority44-import", domain, reportId: selectedReportId });
    if (auditContextRef.current !== auditContextKey) return;
    if (audit) {
      setLiveAuditData(audit.structured_data);
      setLastAuditMeta({ region: audit.region, domain: audit.domain, createdAt: audit.created_at });
      return;
    } else {
      setLiveAuditData(null);
      setLastAuditMeta(null);
    }
  };

  useEffect(() => {
    if (!userId) return;
    loadLatestAudits({ userId, cardId: "ai-readiness", limit: 50 }).then((audits) => {
      const found = new Set<string>();
      for (const a of audits) found.add(a.shortcut_id);
      setAvailableAudits(found);
    }).catch((err) => console.warn("[AIReadiness] Failed to load:", err));
    loadDashboardAudit(activeSite?.domain);
  }, [userId]);

  useEffect(() => {
    if (activeSite?.domain && userId) loadDashboardAudit(activeSite.domain);
    else {
      setLiveAuditData(null);
      setLastAuditMeta(null);
    }
  }, [activeSite?.domain, userId, selectedReportId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const mainEl = document.querySelector("main");
    if (mainEl) mainEl.scrollTop = 0;
  }, []);

  const handleSubNavClick = async (item: SubNavItem) => {
    if (!userId) return;
    setActiveSubNav(item.id);
    const saved = await loadLatestAuditByShortcut({ userId, cardId: "ai-readiness", shortcutId: item.shortcutId, domain: activeSite?.domain });
    if (auditContextRef.current !== auditContextKey) return;
    if (saved) {
      setLiveAuditData(saved.structured_data as any);
      setLastAuditMeta({ region: saved.region, domain: saved.domain, createdAt: saved.created_at });
    } else {
      setLiveAuditData(null);
    }
  };

  const handleAuditComplete = (result: any) => {
    setLiveAuditData(result);
    setLastAuditMeta({ region: "", domain: result.domain || "", createdAt: new Date().toISOString() });
    if (result.requestedAuditType) {
      setAvailableAudits((prev) => new Set([...prev, result.requestedAuditType]));
    }
  };

  const ai = liveAuditData?.aiVisibility;
  const engines: Record<string, any> = ai?.engines ?? {};
  const leaderboard: any[] = ai?.leaderboard ?? [];
  const engineList = Object.values(engines).filter((e: any) => !e.error);

  const avgBrand = engineList.length ? Math.round(engineList.reduce((s: number, e: any) => s + (e.brandPresence ?? 0), 0) / engineList.length) : 0;
  const avgLink = engineList.length ? Math.round(engineList.reduce((s: number, e: any) => s + (e.linkPresence ?? 0), 0) / engineList.length) : 0;
  const avgPos = engineList.length ? (engineList.reduce((s: number, e: any) => s + (e.averagePosition ?? 0), 0) / engineList.length).toFixed(1) : "0";

  const defaults = heroDefaults[activeTab];
  const score = ai ? avgBrand : 0;
  const subtitle = ai ? `Live audit for ${liveAuditData?.domain ?? "your domain"} — ${engineList.length} AI engines tracked` : defaults.subtitle;
  const metrics = ai ? [
    { label: "Brand Presence", value: `${avgBrand}%` },
    { label: "Link Presence", value: `${avgLink}%` },
    { label: "Avg. Position", value: avgPos },
    { label: "Engines Tracked", value: String(engineList.length) },
  ] : defaults.metricLabels.map((l) => ({ label: l, value: "0" }));

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden" style={{ background: "#08080E" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0, background: "radial-gradient(620px 420px at 88% -6%, rgba(139,92,246,.15), transparent 70%), radial-gradient(560px 460px at 102% 108%, rgba(255,122,24,.10), transparent 70%)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.4, zIndex: 0, backgroundImage: "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)", backgroundSize: "46px 46px", WebkitMaskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)", maskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)" }} />

      <div className="relative z-[1] px-6 md:px-10 lg:px-14 py-6 mx-auto" style={{ ["--accent" as string]: "#3B82F6", maxWidth: "1480px" } as React.CSSProperties}>
        {/* Header */}
        <div className="flex items-center gap-3.5 mb-1.5">
          <button onClick={() => navigate("/dashboard")} aria-label="Back to dashboard" className="grid place-items-center w-[38px] h-[38px] flex-none rounded-[10px] border border-white/[0.07] bg-og01-card text-og01-text-secondary transition-colors hover:border-white/[0.12] hover:text-og01-text">
            <ArrowLeft className="w-[18px] h-[18px]" />
          </button>
          <SiteSwitcher onSiteChange={(domain) => loadDashboardAudit(domain)} />
          <ReportSwitcher />
          <h1 className="flex items-center gap-2.5 font-heading text-[30px] leading-none text-og01-text">
            <ShieldCheck className="w-[26px] h-[26px]" style={{ color: "var(--accent)" }} />
            AI Readiness
          </h1>
          <div className="flex-1" />
          <button onClick={openFullReportExport} className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] border border-white/[0.07] bg-og01-card text-[13.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12]" style={{ color: liveAuditData ? undefined : "#6B6585" }}>
            <Download className="w-[15px] h-[15px]" />Export Full Report
          </button>
          <button onClick={() => navigate("/resource-vault?card=ai-readiness")} className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] border-none text-[13.5px] font-semibold text-white transition-all hover:brightness-110" style={{ background: "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)", boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
            <FolderOpen className="w-[15px] h-[15px]" />Saved Reports
          </button>
        </div>

        {/* Metadata */}
        {lastAuditMeta && (
          <div className="flex items-center gap-5 mb-4 pl-[52px] text-[12.5px] text-og01-text-muted">
            {[
              lastAuditMeta.domain ? <>Domain: <b className="font-medium text-og01-text-secondary">{lastAuditMeta.domain}</b></> : null,
              <>Engines: <b className="font-medium text-og01-text-secondary">{engineList.length}</b></>,
              lastAuditMeta.createdAt ? <>Last Run: <b className="font-medium text-og01-text-secondary">{formatTimeAgo(lastAuditMeta.createdAt)}</b></> : null,
            ].filter(Boolean).map((item, i) => (
              <span key={i}>{item}</span>
            ))}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-5 p-1.5 rounded-[14px] overflow-x-auto border border-white/[0.07] bg-og01-card">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex items-center gap-2 px-[15px] py-2.5 rounded-[9px] text-[13.5px] font-medium whitespace-nowrap transition-all hover:text-og01-text"
                style={isActive ? { color: "var(--accent)", background: "color-mix(in srgb, var(--accent) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 28%, transparent)" } : { color: "#9AA0B3", border: "1px solid transparent" }}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        {/* Score Gauge with Sub-Nav */}
        <div className="mb-6">
          <ScoreGaugePanel
            score={score} scoreChange={0} period="Last 30 Days" subtitle={subtitle} metrics={metrics}
            badge={defaults.badge} title={defaults.title} centerLabel={defaults.centerLabel}
            subNavItems={TAB_SUBNAV[activeTab].map((item) => ({ ...item, hasData: availableAudits.has(item.shortcutId) }))}
            activeSubNav={activeSubNav} onSubNavClick={handleSubNavClick}
          />
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6" style={{ minHeight: "calc(100vh - 200px)" }}>
            <ActionPlanIssuesPanel cardId="ai-readiness" title="Recommended Actions" />

            {/* AI Engine Breakdown — shown for all tabs when data exists */}
            {ai ? (
              <>
                <DataPanel title="AI Engine Breakdown" icon={<Cpu className="w-4 h-4" />} actions={<WidgetExportBtn title="AI Engine Breakdown" data={flattenAIEngines(engines)} />}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                          {["Engine", "Brand Presence", "Link Presence", "Avg. Position"].map((h) => (
                            <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(engines).map(([name, eng]: [string, any], i) => (
                          <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                            <td className="py-2.5 px-2 font-medium" style={{ color: "#8B7BF7" }}>{name}</td>
                            {eng.error ? (
                              <td colSpan={3} className="py-2.5 px-2 text-xs" style={{ color: "#F87171" }}>{eng.error}</td>
                            ) : (
                              <>
                                <td className="py-2.5 px-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-16 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                                      <div className="h-full rounded-full" style={{ width: `${eng.brandPresence ?? 0}%`, background: "#FF9A4A" }} />
                                    </div>
                                    <span className="text-xs font-bold" style={{ color: "#FF9A4A" }}>{eng.brandPresence ?? 0}%</span>
                                  </div>
                                </td>
                                <td className="py-2.5 px-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-16 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                                      <div className="h-full rounded-full" style={{ width: `${eng.linkPresence ?? 0}%`, background: "#4ADE80" }} />
                                    </div>
                                    <span className="text-xs font-bold" style={{ color: "#4ADE80" }}>{eng.linkPresence ?? 0}%</span>
                                  </div>
                                </td>
                                <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(eng.averagePosition ?? 0).toFixed(1)}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </DataPanel>

                {/* AI Leaderboard */}
                {leaderboard.length > 0 && (
                  <DataPanel title="AI Leaderboard" icon={<ShieldCheck className="w-4 h-4" />} actions={<WidgetExportBtn title="AI Leaderboard" data={flattenLeaderboard(leaderboard)} />}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                            {["#", "Domain", "Brand Presence", "Link Presence", "Avg. Position"].map((h) => (
                              <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {leaderboard.slice(0, 15).map((entry: any, i: number) => (
                            <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                              <td className="py-2.5 px-2 font-bold" style={{ color: i < 3 ? "#FF9A4A" : "#9AA0B3" }}>{i + 1}</td>
                              <td className="py-2.5 px-2 font-mono text-xs" style={{ color: entry.domain === liveAuditData?.domain ? "#8B7BF7" : "#C4BDE0" }}>
                                {entry.domain}{entry.domain === liveAuditData?.domain && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(91,61,245,0.2)", color: "#8B7BF7" }}>You</span>}
                              </td>
                              <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{entry.brandPresence ?? 0}%</td>
                              <td className="py-2.5 px-2" style={{ color: "#4ADE80" }}>{entry.linkPresence ?? 0}%</td>
                              <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(entry.averagePosition ?? 0).toFixed(1)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </DataPanel>
                )}

                {/* Summary Metrics */}
                <DataPanel title="AI Visibility Summary" icon={<Database className="w-4 h-4" />} actions={<WidgetExportBtn title="AI Visibility Summary" data={[{ "Avg Brand %": avgBrand, "Avg Link %": avgLink, "Avg Position": avgPos, Engines: Object.keys(engines).length, "With Data": engineList.length, Competitors: leaderboard.length }]} />}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <MetricCard label="Avg. Brand Presence" value={`${avgBrand}%`} highlight />
                    <MetricCard label="Avg. Link Presence" value={`${avgLink}%`} />
                    <MetricCard label="Avg. Position" value={avgPos} />
                    <MetricCard label="Engines Tracked" value={String(Object.keys(engines).length)} />
                    <MetricCard label="Engines with Data" value={String(engineList.length)} />
                    <MetricCard label="Competitors" value={String(leaderboard.length)} />
                  </div>
                </DataPanel>
              </>
            ) : (
              <>
                <DataPanel title="AI Engine Breakdown" icon={<Cpu className="w-4 h-4" />} actions={<WidgetExportBtn title="AI Engine Breakdown" data={[]} />}>
                  <EmptyState title="No AI engine data yet" description="Run an AI Visibility, Engine Comparison, or Maturity audit to see engine-level metrics." />
                </DataPanel>
                <DataPanel title="AI Leaderboard" icon={<ShieldCheck className="w-4 h-4" />} actions={<WidgetExportBtn title="AI Leaderboard" data={[]} />}>
                  <EmptyState title="No leaderboard data yet" description="Run an AI audit to see how you rank against competitors in AI engines." />
                </DataPanel>
                <DataPanel title="AI Visibility Summary" icon={<Database className="w-4 h-4" />} actions={<WidgetExportBtn title="AI Visibility Summary" data={[]} />}>
                  <EmptyState title="No visibility data yet" description="Run any AI Readiness audit to see your summary metrics." />
                </DataPanel>
              </>
            )}
          </div>

          {/* Right column — Ask Ruby Chat */}
          <div className="lg:col-span-2 flex flex-col">
            <div className="sticky top-6 flex-1" style={{ height: "calc(100vh - 48px)" }}>
              <AskRubyAIReadinessWidget activeTab={activeTab} onIssueFixed={() => loadDashboardAudit(activeSite?.domain)} />
            </div>
          </div>
        </div>
      </div>

      <RubyExportDialog
        open={exportDialog.open}
        onClose={() => setExportDialog((p) => ({ ...p, open: false }))}
        title={exportDialog.title}
        tier={exportDialog.tier}
        data={exportDialog.data}
        fileName={`OG01_AI_Readiness_${lastAuditMeta?.domain || "report"}`}
        auditResult={exportDialog.auditResult}
        domain={lastAuditMeta?.domain}
      />
    </div>
  );
};

export default AIReadinessDashboard;

